/**
 * FirstQuoteStep - Design System v2
 * Matching Login page aesthetic with glassmorphism
 * OPTIONNEL - Premier devis
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, SkipForward, Loader2, Plus, Trash2 } from 'lucide-react';
import { quotesAPI, companiesAPI } from '@/utils/api';
import { clientsAPI } from '@/utils/clientsAPI';
import { productsAPI } from '@/utils/productsAPI';
import { taxRatesAPI, type TaxRate } from '@/utils/taxRatesAPI';
import { logger } from '@/utils/logger';
import { notifications } from '@/lib/notifications';

interface FirstQuoteStepProps {
  onComplete: () => void;
  onSkip: () => void;
}

interface QuoteItem {
  id: string;
  productId?: string;
  description: string;
  quantity: number;
  unitPrice: number;
  taxRateId: string;
  taxPercent: number;
}

interface Client {
  id: string;
  fullName?: string;
  companyName?: string;
  email?: string;
}

interface Product {
  id: string;
  name: string;
  description?: string;
  unitPrice: number;
}

// Glass Button component
const GlassButton: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'ghost';
  isLoading?: boolean;
  className?: string;
  type?: 'button' | 'submit';
}> = ({ children, onClick, disabled, variant = 'primary', isLoading, className = '', type = 'button' }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled || isLoading}
    className={`
      flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-semibold transition-all duration-300
      ${variant === 'primary'
        ? 'bg-gradient-to-r from-lime-400 to-lime-500 text-teal-800 hover:shadow-lg hover:shadow-lime-500/30 hover:-translate-y-0.5 disabled:opacity-50'
        : variant === 'ghost'
        ? 'text-white/60 hover:text-white hover:bg-white/10'
        : 'bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 hover:border-white/30'
      }
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      ${className}
    `}
  >
    {isLoading ? (
      <div className="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent" />
    ) : children}
  </button>
);

export const FirstQuoteStep: React.FC<FirstQuoteStepProps> = ({ onComplete, onSkip }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [clients, setClients] = useState<Client[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [taxRates, setTaxRates] = useState<TaxRate[]>([]);
  const [defaultTaxRateId, setDefaultTaxRateId] = useState<string>('');
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedClientId, setSelectedClientId] = useState('');
  const [items, setItems] = useState<QuoteItem[]>([]);
  const [newItem, setNewItem] = useState({
    description: '',
    quantity: 1,
    unitPrice: 0,
    taxRateId: '',
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoadingData(true);
        logger.log('📥 Chargement clients, produits et taux de TVA...');

        const [clientsRes, productsRes, taxRatesRes] = await Promise.all([
          clientsAPI.getAll({ page: 1, limit: 100 }),
          productsAPI.getAll({ limit: 100 }),
          taxRatesAPI.getAll({ activeOnly: true }),
        ]);

        setClients(clientsRes.clients || []);
        setProducts(productsRes.data || []);
        setTaxRates(taxRatesRes.taxRates || []);

        const defaultRate = taxRatesRes.taxRates?.find((tr: TaxRate) => tr.isDefault || Number(tr.rate) === 20) || taxRatesRes.taxRates?.[0];
        if (defaultRate) {
          setDefaultTaxRateId(defaultRate.id);
          setNewItem(prev => ({ ...prev, taxRateId: defaultRate.id }));
        }

        if (clientsRes.clients && clientsRes.clients.length > 0) {
          setSelectedClientId(clientsRes.clients[0].id);
        }
      } catch (err: any) {
        logger.error('❌ Erreur chargement données:', err);
        setClients([]);
        setProducts([]);
        setTaxRates([]);
      } finally {
        setIsLoadingData(false);
      }
    };

    loadData();
  }, []);

  const addItem = () => {
    if (!newItem.description || !newItem.taxRateId) return;

    const selectedTaxRate = taxRates.find(tr => tr.id === newItem.taxRateId);
    if (!selectedTaxRate) return;

    const item: QuoteItem = {
      id: Date.now().toString(),
      description: newItem.description,
      quantity: newItem.quantity,
      unitPrice: newItem.unitPrice,
      taxRateId: newItem.taxRateId,
      taxPercent: Number(selectedTaxRate.rate),
    };

    setItems([...items, item]);
    setNewItem({
      description: '',
      quantity: 1,
      unitPrice: 0,
      taxRateId: defaultTaxRateId,
    });
  };

  const removeItem = (id: string) => {
    setItems(items.filter((item) => item.id !== id));
  };

  const addProductAsItem = (product: Product) => {
    const existingItemIndex = items.findIndex(item => item.productId === product.id);

    if (existingItemIndex !== -1) {
      const updatedItems = [...items];
      updatedItems[existingItemIndex].quantity += 1;
      setItems(updatedItems);
      notifications.success(t('onboarding.quote.quantityIncreased', { name: product.name }));
      return;
    }

    const item: QuoteItem = {
      id: Date.now().toString(),
      productId: product.id,
      description: product.name,
      quantity: 1,
      unitPrice: Number(product.unitPrice),
      taxRateId: defaultTaxRateId,
      taxPercent: Number(taxRates.find(tr => tr.id === defaultTaxRateId)?.rate ?? 0),
    };
    setItems([...items, item]);
    notifications.success(t('onboarding.quote.productAdded', { name: product.name }));
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => {
      const totalHT = item.quantity * item.unitPrice;
      const totalTTC = totalHT * (1 + item.taxPercent / 100);
      return sum + totalTTC;
    }, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedClientId) {
      setError(t('onboarding.quote.selectClient'));
      return;
    }

    if (items.length === 0) {
      setError(t('onboarding.quote.addAtLeastOneLine'));
      return;
    }

    try {
      setIsSubmitting(true);

      const companies = await companiesAPI.getAll();
      if (!companies || companies.length === 0) {
        throw new Error(t('onboarding.quote.noCompany'));
      }
      const company = companies[0];

      const today = new Date();
      const validUntil = new Date();
      validUntil.setDate(today.getDate() + 30);

      const quoteData = {
        clientId: selectedClientId,
        issueDate: today,
        validUntil: validUntil,
        items: items.map((item, index) => ({
          productId: item.productId,
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          taxRateId: item.taxRateId,
          taxPercent: item.taxPercent,
          position: index,
        })),
      };

      const createdQuote = await quotesAPI.create(quoteData as any);
      logger.log('✅ Devis créé avec succès', createdQuote);
      notifications.success(t('onboarding.quote.createSuccess'));

      if (createdQuote?.id) {
        navigate(`/quotes/${createdQuote.id}`);
      } else {
        onComplete();
      }
    } catch (err: any) {
      logger.error('❌ Erreur création devis:', err);
      setError(err.message || t('onboarding.quote.createError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingData) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-12 border border-white/20">
          <Loader2 className="w-10 h-10 text-lime-400 animate-spin mx-auto mb-4" />
          <p className="text-white font-medium">{t('onboarding.quote.loadingData')}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">
          {t('onboarding.quote.title')}
        </h1>
        <p className="text-white/60">
          {t('onboarding.quote.subtitle')}
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-500/20 backdrop-blur-md border border-red-400/30 rounded-2xl text-white">
          {error}
        </div>
      )}

      {(clients.length === 0 || products.length === 0) && (
        <div className="mb-6 p-4 bg-yellow-500/20 backdrop-blur-md border border-yellow-400/30 rounded-2xl text-white">
          {clients.length === 0 && t('onboarding.quote.noClient')}
          {products.length === 0 && t('onboarding.quote.noProduct')}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Client Selection */}
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/20 shadow-2xl">
          <label className="block text-sm font-medium text-white/80 mb-2">
            {t('onboarding.quote.client')} <span className="text-lime-400">*</span>
          </label>
          <select
            value={selectedClientId}
            onChange={(e) => setSelectedClientId(e.target.value)}
            className="w-full px-4 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl text-white focus:outline-none focus:ring-2 focus:ring-lime-400/50 focus:border-lime-400/50 transition-all duration-300"
            required
          >
            <option value="" className="bg-teal-800">{t('onboarding.quote.selectClientOption')}</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id} className="bg-teal-800">
                {client.companyName || client.fullName || client.email}
              </option>
            ))}
          </select>
        </div>

        {/* Quick Add from Products */}
        {products.length > 0 && (
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/20 shadow-2xl">
            <label className="block text-sm font-medium text-white/80 mb-4">
              {t('onboarding.quote.addFromProducts')}
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {products.map((product) => (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => addProductAsItem(product)}
                  className="p-4 text-left bg-white/5 border border-white/10 rounded-2xl hover:border-lime-400/50 hover:bg-white/10 transition-all duration-300 group"
                >
                  <p className="font-medium text-white group-hover:text-lime-400 transition-colors">
                    {product.name}
                  </p>
                  <p className="text-sm text-white/50 mt-1">
                    {Number(product.unitPrice).toFixed(2)} €
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Add New Item */}
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/20 shadow-2xl">
          <h3 className="text-sm font-medium text-white mb-4">
            {t('onboarding.quote.addCustomLine')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
            <div className="md:col-span-2">
              <input
                placeholder={t('onboarding.quote.descriptionField')}
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                className="w-full px-4 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-lime-400/50"
              />
            </div>
            <input
              type="number"
              placeholder={t('onboarding.quote.quantity')}
              value={newItem.quantity}
              onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}
              min={1}
              className="w-full px-4 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-lime-400/50"
            />
            <input
              type="number"
              placeholder={t('onboarding.quote.priceHT')}
              value={newItem.unitPrice}
              onChange={(e) => setNewItem({ ...newItem, unitPrice: parseFloat(e.target.value) || 0 })}
              step={0.01}
              min={0}
              className="w-full px-4 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-lime-400/50"
            />
            <select
              value={newItem.taxRateId}
              onChange={(e) => setNewItem({ ...newItem, taxRateId: e.target.value })}
              className="w-full px-4 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl text-white focus:outline-none focus:ring-2 focus:ring-lime-400/50"
            >
              <option value="" className="bg-teal-800">{t('onboarding.quote.vatField')}</option>
              {taxRates.map((rate) => (
                <option key={rate.id} value={rate.id} className="bg-teal-800">
                  TVA {Number(rate.rate).toFixed(0)}%
                </option>
              ))}
            </select>
          </div>
          <GlassButton
            type="button"
            onClick={addItem}
            disabled={!newItem.description || !newItem.taxRateId}
            variant="secondary"
          >
            <Plus className="w-4 h-4" />
            {t('onboarding.quote.add')}
          </GlassButton>
        </div>

        {/* Items List */}
        {items.length > 0 && (
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 overflow-hidden shadow-2xl">
            <div className="bg-white/5 px-6 py-3 border-b border-white/10">
              <p className="text-sm font-medium text-white">
                {t('onboarding.quote.quoteLines', { count: items.length })}
              </p>
            </div>
            <div className="divide-y divide-white/10">
              {items.map((item) => (
                <div key={item.id} className="p-4 flex items-center justify-between hover:bg-white/5 transition-colors">
                  <div className="flex-1">
                    <p className="font-medium text-white">{item.description}</p>
                    <p className="text-sm text-white/50">
                      {item.quantity} × {item.unitPrice.toFixed(2)} € (TVA {item.taxPercent.toFixed(0)}%)
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="font-semibold text-lime-400">
                      {(item.quantity * item.unitPrice * (1 + item.taxPercent / 100)).toFixed(2)} €
                    </p>
                    <button
                      type="button"
                      onClick={() => removeItem(item.id)}
                      className="p-2 text-red-400 hover:bg-red-500/20 rounded-xl transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-white/5 px-6 py-4 border-t border-white/20">
              <div className="flex justify-between items-center">
                <p className="font-medium text-white">{t('onboarding.quote.totalTTC')}</p>
                <p className="text-2xl font-bold text-lime-400">
                  {calculateTotal().toFixed(2)} €
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between items-center pt-4">
          <GlassButton
            type="button"
            onClick={onSkip}
            disabled={isSubmitting}
            variant="ghost"
          >
            <SkipForward className="w-5 h-5" />
            {t('onboarding.quote.skip')}
          </GlassButton>

          <GlassButton
            type="submit"
            disabled={!selectedClientId || items.length === 0 || isSubmitting}
            isLoading={isSubmitting}
          >
            {t('onboarding.quote.createButton')}
            {!isSubmitting && <ArrowRight className="w-5 h-5" />}
          </GlassButton>
        </div>
      </form>
    </div>
  );
};
